import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import TransformationMapper from "./mappers/TransformationMapper";
import DataTableReadMapper from "./mappers/DataTableReadMapper";
import GroupFilterMapper from "./mappers/GroupFilterMapper";

import DataTableReadDto from "../classes/dtos/pathcommonmodule/DataTableReadDto";

import GetPathAIDetailResult from "../classes/api/result/pathaimodule/GetPathAIDetailResult";
import UpdatePathAIDetailResult from "../classes/api/result/pathaimodule/UpdatePathAIDetailResult";

import CreateRequestPathAIResult from "../classes/api/result/pathaimodule/CreateRequestPathAIResult";
import GetRequestPathAIResult from "../classes/api/result/pathaimodule/GetRequestPathAIResult";
import PathParameterDto from "../classes/dtos/pathcommonmodule/PathParameterDto";

import GetPathAICapabilitiesResult from "../classes/api/result/pathaimodule/GetPathAICapabilitiesResult";

import PathAIMappingDto from "../classes/dtos/pathaimodule/PathAIMappingDto";
import ScheduleDto from "../classes/dtos/schedulermodule/ScheduleDto";

import FieldReferenceIdAndKeyDto from "../classes/dtos/FieldReferenceIdAndKeyDto";
import FieldTypeReferenceDto from "../classes/dtos/FieldTypeReferenceDto";
import PathAIMappingCapabilitiesDto from "../classes/dtos/pathaimodule/PathAIMappingCapabilitiesDto";

const PathAIApi = {

    GetPathAIDetailAsync: async function (pathId: number): Promise<ProcessingResult<GetPathAIDetailResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathAI/GetPathAIDetail?pathId=" + pathId.toString(), {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let source = DataTableReadMapper.ToDto(data.source);
            let mappings = data.mappings.map((e: any) => new PathAIMappingDto(e.fieldReferenceId, e.transformationLogicId, e.transformationParameters, e.displayName, e.description, e.isSelected, GroupFilterMapper.RowFiltersToDtos(e.filters), GroupFilterMapper.DeclaredVariablesFromResponse(e.declaredVariables)));
            let schedule = new ScheduleDto(data.schedule.isActive, data.schedule.scheduleType, data.schedule.interval, data.schedule.time);
            return ProcessingResult.Ok(new GetPathAIDetailResult(data.id, data.name, source, mappings, schedule, data.lastVersionId, data.publishedVersionId, data.advancedFilterMode ?? false));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdatePathAIDetailAsync: async function (pathId: number, name: string, mappings: PathAIMappingDto[], source: DataTableReadDto, schedule: ScheduleDto, advancedFilterMode: boolean, publish: boolean): Promise<ProcessingResult<UpdatePathAIDetailResult>> {
        let authSession = await AuthToken.get();

        let time = (schedule.Time / (60 * 1000)) - new Date().getTimezoneOffset();

        const response = await fetch(Settings.api_url + "/PathAI/UpdatePathAIDetail", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'name': name,
                'source': DataTableReadMapper.ToPayload(source),
                'mappings': mappings.map((e) => ({ fieldReferenceId: e.FieldReferenceId, transformationLogicId: e.TransformationLogicId, transformationParameters: e.TransformationParameters, displayName: e.DisplayName, description: e.Description, isSelected: e.IsSelected, filters: GroupFilterMapper.RowFiltersToPayload(e.Filters), declaredVariables: GroupFilterMapper.DeclaredVariablesToPayload(e.DeclaredVariables) })),
                'schedule': {
                    'isActive': schedule.IsActive,
                    'scheduleType': schedule.Type,
                    'interval': schedule.Interval,
                    'time': time
                },
                'advancedFilterMode': advancedFilterMode,
                'Publish': publish
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new UpdatePathAIDetailResult(data.id, data.isPublished));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    GetPathAICapabilitiesAsync: async function (tableReferenceSourceId: number | null, pathId: number | null): Promise<ProcessingResult<GetPathAICapabilitiesResult>> {
        let authSession = await AuthToken.get();

        let url = "/PathAI/GetPathAICapabilities";
        let separator = "?";

        if (tableReferenceSourceId !== null) { url = url + separator + "TableReferenceSourceId=" + tableReferenceSourceId; separator = "&"; }
        if (pathId !== null) { url = url + separator + "PathId=" + pathId; }

        const response = await fetch(Settings.api_url + url, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let sourceFields = data.sourceFields.map((e: any) => new FieldReferenceIdAndKeyDto(e.id, e.sourceKey, e.datasetKey, e.name, new FieldTypeReferenceDto(e.type.type, e.type.isMultiple, e.type.typeLabel, e.type.isNullable, e.type.maxLength, e.type.isReadOnly, e.type.isCalculated, e.type.isBusinessRequired, e.type.isPrimaryKey, e.type.originTypeLabel, e.type.linkTableId)));
            let mappingsCapabilities = data.mappingsCapabilities.map((e: any) => new PathAIMappingCapabilitiesDto(e.sourceFieldId, e.transformationLogicIds));
            let transformationsCapabilities = data.transformationsCapabilities.map((e: any) => TransformationMapper.ToDto(e));
            return ProcessingResult.Ok(new GetPathAICapabilitiesResult(sourceFields, mappingsCapabilities, transformationsCapabilities));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    // Route 1 (main API): asks an agent to run the PathAI and returns the agent url + task token
    CreateRequestPathAIAsync: async function (pathId: number, fieldReferenceIds: number[] | null, parameters: PathParameterDto[], filterParameters: string | null, signal?: AbortSignal): Promise<ProcessingResult<CreateRequestPathAIResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathAI/CreateRequestPathAI", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            signal: signal,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'fieldReferenceIds': fieldReferenceIds,
                'parameters': parameters.map((e) => ({ parameterName: e.ParameterName, fieldType: e.FieldType, valueString: e.ValueString, valueInt32: e.ValueInt32, valueBoolean: e.ValueBoolean, valueDate: e.ValueDate, valueDateTime: e.ValueDateTime, valueDateTimeOffset: e.ValueDateTimeOffset })),
                'filterParameters': filterParameters
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new CreateRequestPathAIResult(data.accepted, data.url, data.token));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    // Route 2 (agent API): reads the status and one page of rows of a task
    GetRequestPathAIAsync: async function (agentUrl: string, taskId: string, pageToken: string, signal?: AbortSignal): Promise<ProcessingResult<GetRequestPathAIResult>> {
        let authSession = await AuthToken.get();

        let url = agentUrl.replace(/\/+$/, "") + "/PathAI/GetRequestPathAI?TaskId=" + encodeURIComponent(taskId);
        if (pageToken !== "") { url = url + "&PageToken=" + encodeURIComponent(pageToken); }

        const response = await fetch(url, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            signal: signal,
            headers: {
                "Accept": "application/json",
                "Authorization": "TokenFederation " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new GetRequestPathAIResult(data.status, data.rows ?? [], data.nextPage ?? ""));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    }
};

export default PathAIApi;